import type { CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, type ProjectResponse, type ProjectSummaryResponse } from '@/lib/api'
import { buildProjectNumberMap, projectLabelById } from '@/lib/projectLabels'
import { useAuth } from '@/lib/auth'

function StatCard({
  label,
  value,
  accent = false,
  to,
}: {
  label: string
  value: number | string
  accent?: boolean
  to?: string
}) {
  const inner = (
    <div
      className="rounded-xl border p-5 transition-shadow hover:shadow-sm"
      style={{
        borderColor: accent ? 'oklch(0.90 0.05 195)' : 'var(--border)',
        background: accent ? 'oklch(0.97 0.02 195)' : 'var(--surface)',
        borderRadius: 'var(--radius-xl)',
      }}
    >
      <p className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted)' }}>
        {label}
      </p>
      <p
        className="mt-1 text-3xl font-bold"
        style={{ color: accent ? 'var(--accent)' : 'var(--ink)' }}
      >
        {value}
      </p>
    </div>
  )
  return to ? (
    <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
      {inner}
    </Link>
  ) : inner
}

function ProjectHealthRow({
  s,
  projects,
  projectNumbers,
}: {
  s: ProjectSummaryResponse
  projects: ProjectResponse[] | undefined
  projectNumbers: Map<number, number>
}) {
  const hasIssue = s.unreviewed_leads > 0 || s.hot_leads > 0
  return (
    <Link
      to={`/leads?project=${s.project_id}`}
      className="flex items-center justify-between border-b last:border-0"
      style={{
        borderColor: 'var(--border)',
        cursor: 'pointer',
        padding: '10px 4px',
        textDecoration: 'none',
        borderRadius: 4,
        transition: 'background 0.12s ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'rgba(11,15,20,0.03)'
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = ''
      }}
    >
      <div className="flex items-center gap-3">
        {hasIssue && (
          <span
            className="h-2 w-2 rounded-full shrink-0"
            style={{ background: '#FBBF24' }}
          />
        )}
        <span className="text-sm" style={{ color: 'var(--ink)' }}>
          {projectLabelById(s.project_id, projects, projectNumbers)}
        </span>
        <span
          className={[
            'rounded-full px-2 py-0.5 text-xs',
            s.status === 'active'
              ? 'bg-green-50 text-green-700'
              : '',
          ].join(' ')}
          style={s.status !== 'active' ? { background: 'rgba(11,15,20,0.04)', color: 'var(--ink-2)' } : undefined}
        >
          {s.status}
        </span>
      </div>
      <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--ink-2)' }}>
        {s.hot_leads > 0 && (
          <span className="font-medium" style={{ color: '#D97706' }}>
            🔥 {s.hot_leads} горячих
          </span>
        )}
        {s.unreviewed_leads > 0 && (
          <span>{s.unreviewed_leads} без фидбека</span>
        )}
        <span
          className="hover:underline"
          style={{ color: 'var(--accent)' }}
        >
          Лиды →
        </span>
      </div>
    </Link>
  )
}

const BOT_URL = 'https://t.me/sensor_tg_bot'

const TIER_LABEL: Record<string, string> = {
  none: 'Нет тарифа',
  trial: 'Триал',
  monitoring: 'Мониторинг',
  basic: 'Basic',
  pro: 'Pro',
  team: 'Team',
  enterprise: 'Enterprise',
}

type LadderRung = {
  key: string
  name: string
  sub: string
  unlocked: boolean
  unlockTier: string
}

/**
 * Capability strip — product modules available to the current organization.
 * Capability flags come from /me so the tier→module mapping lives in
 * plan_catalog, not hardcoded here.
 */
function LadderStrip() {
  const { user } = useAuth()
  if (!user) return null

  const rungs: LadderRung[] = [
    { key: 'mon', name: 'Мониторинг', sub: 'совпадения по словам', unlocked: true, unlockTier: 'Monitoring' },
    { key: 'ai', name: 'Лиды (AI)', sub: 'AI-фильтр и причина', unlocked: user.has_ai, unlockTier: 'Basic' },
    { key: 'aud', name: 'Аудитория', sub: 'оценка участников', unlocked: user.has_audience, unlockTier: 'Pro' },
    { key: 'cat', name: 'Похожие источники', sub: 'карта источников', unlocked: user.has_source_catalog, unlockTier: 'Pro' },
  ]
  const openCount = rungs.filter((r) => r.unlocked).length
  const firstLocked = rungs.findIndex((r) => !r.unlocked)
  // Highlight the "current frontier" rung only while something is still locked.
  // When everything is unlocked, singling one tile out reads as a bug, so show
  // them all uniformly as done.
  const frontier =
    firstLocked === -1 ? -1 : rungs.reduce((acc, r, i) => (r.unlocked ? i : acc), -1)
  const tierLabel = TIER_LABEL[user.tier] ?? user.tier

  return (
    <div
      className="mb-8"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-lg)',
        padding: '18px 20px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', margin: 0, color: 'var(--ink)' }}>
          Ваши возможности
        </h2>
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--muted)',
            letterSpacing: '0.02em',
          }}
        >
          тариф {tierLabel} · доступно {openCount} из 4
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {rungs.map((r, i) => {
          const state = r.unlocked ? (i === frontier ? 'current' : 'done') : 'locked'
          const isNext = i === firstLocked
          const tile: CSSProperties = {
            padding: '11px 12px',
            borderRadius: 'var(--r-md)',
            border: '1px solid var(--line)',
            background: 'var(--surface-2)',
          }
          if (state === 'current') {
            tile.background = 'var(--accent-soft)'
            tile.borderColor = 'color-mix(in oklab, var(--accent) 34%, transparent)'
          } else if (state === 'locked') {
            tile.opacity = isNext ? 0.92 : 0.66
            if (isNext) {
              tile.borderStyle = 'dashed'
              tile.borderColor = 'color-mix(in oklab, var(--accent) 38%, transparent)'
            }
          }
          return (
            <div key={r.key} style={tile}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: state === 'current' ? 'var(--accent-ink)' : 'var(--muted)',
                }}
              >
                {state === 'done' && (
                  <span style={{ color: 'var(--success)', display: 'inline-flex' }}>
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                )}
                {state === 'current' && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: 'var(--accent)',
                      boxShadow: '0 0 0 3px var(--accent-soft)',
                    }}
                  />
                )}
                {state === 'locked' && (
                  <span style={{ color: 'var(--muted)', display: 'inline-flex' }}>
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <rect x="5" y="11" width="14" height="9" rx="2" />
                      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                    </svg>
                  </span>
                )}
                {state === 'locked' ? r.unlockTier : 'Доступно'}
              </div>
              <div
                style={{
                  marginTop: 5,
                  fontSize: 14,
                  fontWeight: 550,
                  letterSpacing: '-0.01em',
                  color: state === 'current' ? 'var(--accent-ink)' : 'var(--ink)',
                }}
              >
                {r.name}
              </div>
              <div style={{ marginTop: 2, fontSize: 12, color: 'var(--ink-3)' }}>{r.sub}</div>
            </div>
          )
        })}
      </div>

      {firstLocked !== -1 ? (
        <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-3)' }}>
          Следующая возможность — <b style={{ color: 'var(--ink)', fontWeight: 550 }}>{rungs[firstLocked].name}</b>.{' '}
          <a href={BOT_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 550 }}>
            Открыть на {rungs[firstLocked].unlockTier} →
          </a>
        </div>
      ) : (
        <div style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-3)' }}>Максимальный функционал подключён.</div>
      )}
    </div>
  )
}

export function DashboardPage() {
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: api.getProjects,
  })
  const projectNumbers = buildProjectNumberMap(projects)

  const summaryQueries = useQuery({
    queryKey: ['project-summaries', projects?.map((p) => p.id)],
    queryFn: async () => {
      if (!projects?.length) return []
      // Resilient fan-out: a single failing project summary must not zero out
      // the entire dashboard. Promise.all would reject on the first failure,
      // leaving every metric at 0 until a refetch (e.g. navigating away and
      // back) happened to succeed. allSettled keeps the summaries we did get.
      const results = await Promise.allSettled(
        projects.map((p) => api.getProjectSummary(p.id)),
      )
      return results
        .filter(
          (r): r is PromiseFulfilledResult<ProjectSummaryResponse> =>
            r.status === 'fulfilled',
        )
        .map((r) => r.value)
    },
    enabled: !!projects?.length,
  })

  const summaries = summaryQueries.data ?? []

  const totalLeads = summaries.reduce((a, s) => a + s.total_leads, 0)
  const hotLeads = summaries.reduce((a, s) => a + s.hot_leads, 0)
  const unreviewed = summaries.reduce((a, s) => a + s.unreviewed_leads, 0)
  const totalSources = summaries.reduce((a, s) => a + s.sources_count, 0)

  return (
    <div className="p-8 max-w-4xl">
      <div className="ds-pagehead mb-7">
        <h1>Обзор</h1>
      </div>

      <LadderStrip />

      {/* Key metrics.
          The `key` is tied to the aggregate values so the grid re-mounts when
          they change. Without it, the cards mount with 0 while summaries load
          and React fails to patch the text nodes in place when the data arrives
          — leaving every card stuck at 0 on a fresh load even though the fiber
          holds the correct value (the project list below works only because it
          mounts fresh once data exists). Re-mounting guarantees correct paint. */}
      <div
        key={`metrics-${totalLeads}-${hotLeads}-${unreviewed}-${totalSources}`}
        className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-4"
      >
        <StatCard label="Найдено" value={totalLeads} to="/leads" />
        <StatCard
          label="Горячих лидов"
          value={hotLeads}
          accent={hotLeads > 0}
          to="/leads?hot=1"
        />
        <StatCard label="Без фидбека" value={unreviewed} to="/leads?feedback=none" />
        <StatCard label="Источников" value={totalSources} to="/sources" />
      </div>

      {/* Project health list */}
      {summaries.length > 0 && (
        <div
          className="px-5"
          style={{
            borderRadius: 'var(--r-lg)',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
          }}
        >
          <h2 className="py-4 text-sm font-semibold" style={{ color: 'var(--ink-2)' }}>
            Проекты
          </h2>
          {summaries.map((s) => (
            <ProjectHealthRow
              key={s.project_id}
              s={s}
              projects={projects}
              projectNumbers={projectNumbers}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!projects?.length && (
        <div
          className="py-16 text-center"
          style={{
            borderRadius: 'var(--radius-xl)',
            border: '1px dashed var(--border-strong)',
            background: 'var(--surface)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
            Нет проектов. Создайте проект через{' '}
            <a
              href="https://t.me/sensor_tg_bot"
              className="hover:underline"
              style={{ color: 'var(--accent)' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              Telegram-бота
            </a>
            .
          </p>
        </div>
      )}
    </div>
  )
}
